import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";
import type { EditFormOutput } from "../../forms/explorations/EditExplorationForm";

export const useExplorations = ({
  archived = false,
}: { archived?: boolean } = {}) => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    loadExplorations: boolean;
    edit: boolean;
    loadStudents: boolean;
    enrollStudents: boolean;
  }>({
    loadExplorations: false,
    edit: false,
    loadStudents: false,
    enrollStudents: false,
  });

  const loadExplorations = async (
    setExplorations: (data: {
      explorations: any[];
      classes: any[];
      rotations: any[];
      sessions: any[];
      teachers: any[];
    }) => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, loadExplorations: true }));
      const response = await api(
        `/explorations?shape=admin${archived ? "&archived=true" : ""}`,
        { method: "GET" },
        dispatch,
      );
      setExplorations({
        explorations: response.explorations,
        classes: response.classes,
        rotations: response.rotations,
        sessions: response.sessions,
        teachers: response.teachers,
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, loadExplorations: false }));
    }
  };

  const editExploration = async (
    data: EditFormOutput,
    explorationId: string,
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, edit: true }));
      const response = await api(
        `/explorations/${explorationId}`,
        { method: "PUT", body: data },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
      return { exploration: response.exploration, classes: response.classes };
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, edit: true }));
    }
  };

  const loadStudents = async (setStudents: Dispatch<SetStateAction<any[]>>) => {
    try {
      setLoading((prev) => ({ ...prev, loadStudents: true }));
      const response = await api("/auth/users?shape=students");
      setStudents(response.students);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, loadStudents: false }));
    }
  };

  const enrollStudents = async (
    data: string[],
    explorationId: string,
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, enrollStudents: true }));
      const response = await api(
        `/enrollments/admin/${explorationId}`,
        {
          method: "POST",
          body: {
            students: data,
          },
        },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, enrollStudents: false }));
    }
  };

  return {
    loadExplorations,
    editExploration,
    loadStudents,
    enrollStudents,
    loading,
  };
};
